/**
 * The perp order size's 25 / 50 / 75 / MAX chips and slider.
 *
 * A chosen percentage is "sticky": it is re-applied when the TRADER changes
 * something that moves the maximum — leverage, side, order type, the unit —
 * so 50% stays 50% after a leverage change. It is deliberately NOT re-applied
 * when the market moves the maximum (a book tick, a mark update): a size that
 * changes under the cursor is a size the trader did not confirm. If the market
 * shrinks the maximum below the chosen size, the field's validator says so and
 * submit is blocked instead.
 *
 * Any other write to the field — typing, a pasted value, a reset after a
 * successful order, an order-book click — releases the percentage.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Dec, dec } from "../../../utils/amount/decimal";
import { MaxResult } from "../../../utils/amount/max/types";
import { activePreset, fractionOfMax, percentOfMax } from "../../../utils/amount/percent";

export const SIZE_PRESETS = [25, 50, 75] as const;
const ALL_STOPS = [...SIZE_PRESETS, 100];

export interface PerpSizePercentApi {
  /** Slider position, 0-100. */
  sliderPercent: number;
  /** The lit chip (100 = MAX), or null. */
  activePercent: number | null;
  /** Chip, slider or "Available" click: write `pct` of the maximum. */
  setPercent: (pct: number) => void;
  setMax: () => void;
}

export function usePerpSizePercent({
  amount,
  max,
  step,
  followKey,
  write,
}: {
  /** The field's current text. */
  amount: string | undefined;
  max: MaxResult;
  step: Dec;
  /** Changes only when the trader changes leverage, side, order type or unit. */
  followKey: string;
  write: (text: Dec) => void;
}): PerpSizePercentApi {
  const [sticky, setSticky] = useState<number | null>(null);
  // What this hook last wrote. A field value that differs came from somewhere
  // else — the trader, a reset — and releases the percentage.
  const lastWritten = useRef<string | null>(null);
  // Read at re-apply time, so the effect keyed on `followKey` alone still sees
  // the maximum computed in the same render as the change that moved it.
  const maxRef = useRef(max);
  maxRef.current = max;
  const stepRef = useRef(step);
  stepRef.current = step;

  const writeRef = useRef(write);
  writeRef.current = write;

  const apply = useCallback((pct: number) => {
    const current = maxRef.current;
    if (!current.ok) return;
    const next = fractionOfMax(current.max, pct, stepRef.current);
    lastWritten.current = next;
    setSticky(pct);
    writeRef.current(next);
  }, []);

  // Someone other than this hook changed the field: let go of the percentage.
  // Keyed on `amount` alone: the render that sets `sticky` can land before the
  // write it made, and must not read that gap as someone else's edit.
  useEffect(() => {
    if (lastWritten.current === null) return;
    const text = amount ?? "";
    const parsed = dec.parse(text);
    const same =
      text === lastWritten.current ||
      (parsed !== null && dec.eq(parsed, lastWritten.current));
    if (!same) {
      setSticky(null);
      lastWritten.current = null;
    }
  }, [amount]);

  // The trader moved the maximum: carry the chosen percentage across.
  const firstFollow = useRef(true);
  useEffect(() => {
    if (firstFollow.current) {
      firstFollow.current = false;
      return;
    }
    if (sticky === null) return;
    apply(sticky);
    // `sticky` is read, not tracked: only a trader-driven change re-applies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followKey]);

  const value = amount ?? "";
  const sliderPercent =
    sticky ?? (max.ok ? percentOfMax(dec.parse(value) ?? "0", max.max) : 0);
  const activePercent =
    sticky !== null && ALL_STOPS.includes(sticky)
      ? sticky
      : max.ok
      ? activePreset(value, max.max, ALL_STOPS, step)
      : null;

  return {
    sliderPercent,
    activePercent,
    setPercent: apply,
    setMax: useCallback(() => apply(100), [apply]),
  };
}
